import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import FloatingUIElement from "./FloatingUIElement";
import LoomLogoScroller from "./LoomLogoScroller";

/**
 * Vendr-inspired hero section with search functionality and modern design
 */
const VendrHeroSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // Check device size and update responsive states
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobile(width <= 768);
    };

    // Check on initial render
    handleResize();

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Add a scroll handler for the Learn More button
  const scrollToNextSection = () => {
    const nextSection =
      document.getElementById("vendr-companies-section") ||
      document.getElementById("trusted-by-id");

    if (nextSection) {
      const navbarHeight = 80;
      const elementPosition =
        nextSection.getBoundingClientRect().top + window.scrollY - navbarHeight;

      window.scrollTo({
        top: elementPosition,
        behavior: "smooth",
      });
    }
  };

  return (
    <div
      className="vendr-hero-section"
      style={{
        backgroundColor: "#0c0c0c",
        paddingBottom: "20px",
        position: "relative",
      }}
    >
      {/* Main hero content */}
      <div
        className="vendr-hero-main"
        style={{ position: "relative", zIndex: 2 }}
      >
        {/* Floating UI elements with logos - positioned closer to hero text */}
        {!isMobile && (
          <div className="vendr-hero-background-elements">
            <FloatingUIElement
              position={{ top: "25%", left: "10%" }}
              logo="chatgpt.png"
              logoAlt="ChatGPT"
              size="80px"
              logoSize="60%"
              animationDuration={4}
              animationOffset={{
                y: [0, 15, 0],
                rotateX: [0, 0, 0],
                rotateY: [0, 0, 0],
              }}
            />

            <FloatingUIElement
              position={{ bottom: "30%", left: "12%" }}
              logo="quizlet.png"
              logoAlt="Quizlet"
              size="90px"
              logoSize="65%"
              animationDuration={5.5}
              animationOffset={{
                y: [0, 12, 0],
                rotateX: [0, 0, 0],
                rotateY: [0, 0, 0],
              }}
            />

            <FloatingUIElement
              position={{ top: "30%", right: "10%" }}
              logo="grammarly.png"
              logoAlt="Grammarly"
              size="100px"
              logoSize="70%"
              animationDuration={4.8}
              animationOffset={{
                y: [0, 18, 0],
                rotateX: [0, 0, 0],
                rotateY: [0, 0, 0],
              }}
            />

            <FloatingUIElement
              position={{ bottom: "25%", right: "12%" }}
              logo="chegg.png"
              logoAlt="Chegg"
              size="70px"
              logoSize="60%"
              animationDuration={6.2}
              animationOffset={{
                y: [0, 10, 0],
                rotateX: [0, 0, 0],
                rotateY: [0, 0, 0],
              }}
            />
          </div>
        )}

        <div className="vendr-hero-container">
          {/* Main headline - Original typography */}
          <h1
            className="hero-main-title"
            style={{
              fontSize: isMobile
                ? screenWidth <= 480
                  ? "56px"
                  : "64px"
                : screenWidth <= 1200
                ? "72px"
                : "80px",
              lineHeight: 1.1,
              fontWeight: 700,
              marginBottom: isMobile ? "16px" : "24px",
              marginTop: isMobile ? "15px" : "0",
              color: "#ffffff",
              textAlign: "center",
              maxWidth: "900px",
              letterSpacing: "-0.02em",
            }}
          >
            Study <span className="hero-highlight">Smarter</span> Not Harder
          </h1>

          {/* Subtitle - Different for mobile and desktop */}
          <p
            className="hero-subtitle"
            style={{
              fontSize: isMobile ? "18px" : "24px",
              lineHeight: 1.5,
              color: "rgba(255, 255, 255, 0.8)",
              marginBottom: isMobile ? "24px" : "40px",
              maxWidth: "600px",
              textAlign: "center",
              fontWeight: 400,
            }}
          >
            {isMobile
              ? "All the study tools you need to thrive as a student in one single platform."
              : "All the tools you need as a student in one platform."}
          </p>

          {/* CTA Buttons */}
          <div className="vendr-cta-container">
            <Link
              to="/download"
              className="button"
              style={{
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? "auto" : "200px",
              }}
            >
              Get Started
              <RocketLaunchIcon
                sx={{
                  ml: 1,
                  fontSize: "20px",
                  color: "white",
                }}
              />
            </Link>

            <a
              href="#vendr-companies-section"
              onClick={(e) => {
                e.preventDefault();
                scrollToNextSection();
              }}
              className="button secondary"
              style={{
                width: isMobile ? "100%" : "auto",
                minWidth: isMobile ? "auto" : "200px",
              }}
            >
              Learn More
              <ArrowForwardIcon
                sx={{
                  ml: 1,
                  fontSize: "20px",
                  color: "inherit",
                }}
              />
            </a>
          </div>
        </div>
      </div>
      {/* Text above logo scroller */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "60px",
        }}
      >
        <h2
          style={{
            background:
              "linear-gradient(135deg, #ffffff 0%, #cccccc 50%, #999999 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontSize: "20px",
            fontWeight: 500,
            margin: 0,
            textAlign: "center",
          }}
        >
          Trusted by Students Across the Nation
        </h2>
      </div>

      {/* Logo scroller section */}
      <div style={{ position: "relative", zIndex: 10 }}>
        <LoomLogoScroller />
      </div>
    </div>
  );
};

export default VendrHeroSection;
